/**
 * Product Gallery Desktop Slider
 * Handles slider button behavior for screens greater than 750px
 * Changes is-active class based on order instead of scrolling
 */

(function() {
  'use strict';

  class ProductGalleryDesktop {
    constructor() {
      this.mediaQuery = window.matchMedia('(min-width: 750px)');
      this.galleries = [];
      
      this.init();
    }

    init() {
      // Find all product media galleries
      const galleryViewers = document.querySelectorAll('[id^="GalleryViewer-"]');
      
      galleryViewers.forEach((viewer) => {
        const gallery = this.setupGallery(viewer);
        if (gallery) {
          this.galleries.push(gallery);
        }
      });

      // Handle resize
      this.mediaQuery.addEventListener('change', () => {
        this.handleResize();
      });
    }

    setupGallery(viewer) {
      const slider = viewer.querySelector('[id^="Slider-Gallery-"]');
      const sliderButtons = viewer.closest('slider-component')?.querySelector('.slider-buttons');
      
      if (!slider || !sliderButtons) return null;

      const prevButton = sliderButtons.querySelector('.slider-button--prev');
      const nextButton = sliderButtons.querySelector('.slider-button--next');
      const currentCounter = sliderButtons.querySelector('.slider-counter--current');
      const totalCounter = sliderButtons.querySelector('.slider-counter--total');
      const slides = Array.from(slider.querySelectorAll('.slider__slide'));

      if (!prevButton || !nextButton || !slides.length) return null;

      const gallery = {
        slider,
        prevButton,
        nextButton,
        currentCounter,
        totalCounter,
        slides,
        currentIndex: 0
      };

      // Add new class to buttons
      prevButton.classList.add('slider-button--desktop');
      nextButton.classList.add('slider-button--desktop');

      // Initialize current index
      this.updateCurrentIndex(gallery);

      // Only activate on desktop
      if (this.mediaQuery.matches) {
        this.activateDesktopMode(gallery);
      }

      return gallery;
    }

    activateDesktopMode(gallery) {
      // Check if already activated
      if (gallery.prevButton.dataset.desktopMode === 'true') {
        return;
      }

      // Mark as activated
      gallery.prevButton.dataset.desktopMode = 'true';
      gallery.nextButton.dataset.desktopMode = 'true';

      // Add new class to buttons
      gallery.prevButton.classList.add('slider-button--desktop');
      gallery.nextButton.classList.add('slider-button--desktop');

      // Store original handlers if needed
      const originalPrevHandler = gallery.prevButton.onclick;
      const originalNextHandler = gallery.nextButton.onclick;

      // Remove existing click handlers by removing and re-adding
      const newPrevButton = gallery.prevButton.cloneNode(true);
      const newNextButton = gallery.nextButton.cloneNode(true);
      gallery.prevButton.parentNode.replaceChild(newPrevButton, gallery.prevButton);
      gallery.nextButton.parentNode.replaceChild(newNextButton, gallery.nextButton);
      
      gallery.prevButton = newPrevButton;
      gallery.nextButton = newNextButton;
      gallery.prevButton.classList.add('slider-button--desktop');
      gallery.nextButton.classList.add('slider-button--desktop');
      gallery.prevButton.dataset.desktopMode = 'true';
      gallery.nextButton.dataset.desktopMode = 'true';

      // Add click handlers with higher priority
      gallery.prevButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.handlePrevious(gallery);
      }, true); // Use capture phase

      gallery.nextButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.handleNext(gallery);
      }, true); // Use capture phase

      // Update button states
      this.updateButtonStates(gallery);
    }

    deactivateDesktopMode(gallery) {
      // Let default slider behavior handle it
      // Just ensure buttons have the class
      gallery.prevButton.classList.add('slider-button--desktop');
      gallery.nextButton.classList.add('slider-button--desktop');
    }

    updateCurrentIndex(gallery) {
      const activeSlide = gallery.slides.findIndex(slide => slide.classList.contains('is-active'));
      gallery.currentIndex = activeSlide >= 0 ? activeSlide : 0;
    }

    handlePrevious(gallery) {
      if (gallery.currentIndex <= 0) return;

      gallery.currentIndex--;
      this.setActiveSlide(gallery, gallery.currentIndex);
    }

    handleNext(gallery) {
      if (gallery.currentIndex >= gallery.slides.length - 1) return;

      gallery.currentIndex++;
      this.setActiveSlide(gallery, gallery.currentIndex);
    }

    setActiveSlide(gallery, index) {
      // Remove is-active from all slides
      gallery.slides.forEach(slide => slide.classList.remove('is-active'));
      
      // Add is-active to the selected slide
      if (gallery.slides[index]) {
        gallery.slides[index].classList.add('is-active');
      }

      // Update counter (1-based for display)
      if (gallery.currentCounter) {
        gallery.currentCounter.textContent = index + 1;
      }

      // Update button states
      this.updateButtonStates(gallery);

      // Scroll to active slide if needed
      this.scrollToActiveSlide(gallery);
    }

    updateButtonStates(gallery) {
      // Disable prev button if at first slide
      if (gallery.currentIndex === 0) {
        gallery.prevButton.setAttribute('disabled', 'disabled');
      } else {
        gallery.prevButton.removeAttribute('disabled');
      }

      // Disable next button if at last slide
      if (gallery.currentIndex >= gallery.slides.length - 1) {
        gallery.nextButton.setAttribute('disabled', 'disabled');
      } else {
        gallery.nextButton.removeAttribute('disabled');
      }
    }

    scrollToActiveSlide(gallery) {
      const activeSlide = gallery.slides[gallery.currentIndex];
      if (activeSlide) {
        activeSlide.scrollIntoView({ 
          behavior: 'smooth', 
          block: 'nearest',
          inline: 'center'
        });
      }
    }

    handleResize() {
      this.galleries.forEach(gallery => {
        this.updateCurrentIndex(gallery);
        
        if (this.mediaQuery.matches) {
          this.activateDesktopMode(gallery);
        } else {
          this.deactivateDesktopMode(gallery);
        }
      });
    }
  }

  // Initialize when DOM is ready and after a short delay to ensure other scripts have loaded
  function init() {
    // Wait a bit to ensure slider-component is initialized
    setTimeout(() => {
      new ProductGalleryDesktop();
    }, 100);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();

